import { getSessionStorage } from '@src/utils/enhancedSessionStorage'

import isBrowser from '../../utils/isBrowser'

import { InventoryResponce } from './inventory.service'

const INVENTORY_STORAGE_KEY = 'inventory_data'

type InventoryData = Record<string, InventoryResponce>

/** Stored shape: variant data plus optional response meta (code, message, statusText) */
type StoredInventorySession = InventoryData & {
  code?: number
  message?: string
  statusText?: string
}

/** Return type: variant data + code when variant exists, otherwise full session data */
export type InventorySessionResult = { variant: InventoryResponce; code?: number } | (StoredInventorySession | null)

/**
 * Get inventory data from sessionStorage.
 * - When variant exists: returns { variant, code } (variant data + response status code).
 * - When variant not found: returns full session data (whatever is present: code, message, statusText, etc.).
 */
export function getInventoryFromSessionStorage(variantId: string): InventorySessionResult {
  try {
    if (!isBrowser()) return null
    const storage = getSessionStorage()
    const session = storage.getItemJSON<StoredInventorySession>(INVENTORY_STORAGE_KEY, { isGlobal: true })

    const variantData = session?.[variantId]
    if (variantData != null) {
      return { variant: variantData, code: session?.code }
    }
    return session
  } catch (error) {
    console.error('Error getting inventory from sessionStorage:', error)
    return null
  }
}

/**
 * Store inventory data in sessionStorage
 * - On success: { data, code } (code = HTTP status e.g. 200)
 * - On error: { data: {}, code, message, statusText?, stack? } (code = HTTP status e.g. 400, 500, or undefined for network errors)
 */
export function setInventoryInSessionStorage(payload: {
  data?: Record<string, InventoryResponce>
  code?: number
  message?: string
  statusText?: string
}): void {
  try {
    if (!isBrowser()) return
    const { data = {}, code, message, statusText } = payload ?? {}
    const storage = getSessionStorage()
    storage.setItemJSON(INVENTORY_STORAGE_KEY, { ...data, code, message, statusText }, { isGlobal: true })
  } catch (error) {
    console.error('Error storing inventory in sessionStorage:', error)
  }
}
